import React, { useEffect, useState } from 'react';
import { HistoryService } from '@rocheglobal/component-library/src/services/history';
import { useEffectOnce, useEvent } from 'react-use';
import { Props } from '../types';
import { blokToComponent, filterProps } from '../helpers';
import { CludoSearchService } from '../../services/cludo-search';

export enum CludoCategory {
  PAGES = 'pages',
  STORIES = 'stories',
  DOCUMENTS = 'documents',
}

interface SearchParams {
  query: string,
  category: number,
  page: number
}

const resultsPerPage = 6;

const categories = [
  CludoCategory.PAGES,
  CludoCategory.STORIES,
  CludoCategory.DOCUMENTS,
];

export const RocheSearch = ({ blok, slot, getComponent }: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [totalResultsFound, setTotalResultsFound] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(-1);

  const { props, slotted } = filterProps(blok);

  const getStateFromURL = (): SearchParams => {
    const url = HistoryService.getActiveUrl();
    return {
      query: url.searchParams.get('query') || '',
      category: categories.indexOf(url.hash.substring(1) as CludoCategory),
      page: parseInt(url.searchParams.get('page'), 10) || 1,
    };
  };

  const buildSearchQueries = () => categories
    // eslint-disable-next-line compat/compat
    .map((filter: string) => () => CludoSearchService.query({
      search: searchTerm,
      perPage: resultsPerPage,
      page: activePage,
      enableFacetFiltering: true,
      filters: {
        Category: [filter],
      },
    }));

  const querySearch = async () => {
    setIsLoading(true);

    // eslint-disable-next-line compat/compat
    const results = await Promise.all(buildSearchQueries().map((request) => request()));
    const resultsFound = results.map((result) => result.TotalDocument || 0);

    setSearchResults(results);
    setTotalResultsFound(resultsFound);
    setIsLoading(false);
  };

  const handleSearchTermChange = (event) => {
    const value = event.detail;

    setActivePage(1);
    setSearchTerm(value?.trim() || '');
  };

  const handlePageChange = (event) => {
    setActivePage(event.detail);
  };

  const handleCategoryChange = (event) => {
    setActiveCategoryIndex(event.detail);
  };

  const updateStateFromURL = () => {
    const { query, category, page } = getStateFromURL();
    setSearchTerm(query);
    setActiveCategoryIndex(category);
    setActivePage(page);
  };

  useEvent('rocheSearchValueChanged', handleSearchTermChange);
  useEvent('rochePaginationPageChange', handlePageChange);
  useEvent('rocheAccordionTabsGlobalChange', handleCategoryChange);
  useEvent('popstate', updateStateFromURL);

  useEffectOnce(updateStateFromURL);

  useEffect(() => {
    setSearchResults([]);
    setTotalResultsFound([]);

    if (searchTerm !== '') {
      querySearch();
    }
  }, [searchTerm, activePage]);

  useEffect(() => {
    const { query, category, page } = getStateFromURL();

    if (query === searchTerm && category === activeCategoryIndex && page === activePage) {
      return undefined;
    }

    const url = HistoryService.getActiveUrl();
    url.searchParams.set('query', searchTerm);
    url.searchParams.set('page', `${activePage}`);
    window.history.pushState(null, '', url.toString());
    return undefined;
  }, [activeCategoryIndex, searchTerm, activePage]);

  useEffect(() => {
    setActivePage(1);
  }, [activeCategoryIndex]);

  return (
    <roche-search
      {...props}
      is-loading={isLoading}
      search-term={searchTerm}
      active-page={activePage}
      categories={categories}
      results-per-page={resultsPerPage}
      search-results={`${JSON.stringify(searchResults)}`}
      total-results-found={`${JSON.stringify(totalResultsFound)}`}
      slot={slot}
    >
      {Object.entries(slotted)
        .map(([key, value]) => (
          <div slot={key} key={key}>
            {Array.isArray(value)
              ? value.map((item) => blokToComponent({ blok: item, getComponent }, key))
              : blokToComponent({ blok: value, getComponent })}
          </div>
        ))}
    </roche-search>
  );
};
